import { formatDate, formatDateUtc, formatPercent } from "@bw/shared/utils";
import type {
  ContractTermsResponse,
  OrgResponse,
} from "@bw/shared/types/generated";

export function ownerDisplayName(org: OrgResponse): string {
  const rawOwnerId = org.owner_user_id.trim();
  const candidates = [org.owner_display_name, org.owner_full_name];

  for (const candidate of candidates) {
    const name = candidate?.trim();
    if (name && (!rawOwnerId || !name.includes(rawOwnerId))) return name;
  }

  return "Owner unavailable";
}

/** "in_review" -> "in review". Lower-case, underscore-free token label. */
export function formatStatus(status: string): string {
  return status.replaceAll("_", " ");
}

/** Whole days elapsed since an ISO timestamp. */
export function daysSince(iso: string): number {
  return Math.floor(
    (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24),
  );
}

/**
 * Calendar-date label for the date-only contract fields (effective /
 * termination), UTC-pinned so they don't drift a day west of UTC. Returns
 * "Not set" for a missing value rather than an em-dash, matching the
 * approval-detail copy convention.
 */
export function formatOptionalDate(iso?: string | null): string {
  if (!iso) {
    return "Not set";
  }
  return formatDateUtc(iso);
}

export function describeCommercialState(
  terms: ContractTermsResponse | null,
): string {
  if (!terms) {
    return "No published terms";
  }
  if (
    terms.effective_date &&
    new Date(`${terms.effective_date}T00:00:00Z`) > new Date()
  ) {
    return `Scheduled for ${formatOptionalDate(terms.effective_date)}`;
  }
  if (terms.requires_reacceptance) {
    return "Business acceptance pending";
  }
  if (terms.accepted_at) {
    return "Accepted";
  }
  return "Published";
}

export function formatTermsVersion(terms: ContractTermsResponse): string {
  return terms.version_number != null
    ? `v${terms.version_number}`
    : "Legacy terms";
}

export function formatCommissionRate(terms: ContractTermsResponse): string {
  return terms.commission_rate != null
    ? formatPercent(terms.commission_rate)
    : "Not set";
}

export function describeAcceptedTerms(terms: ContractTermsResponse): string {
  if (terms.accepted_at) {
    return `${formatTermsVersion(terms)} accepted ${formatDate(terms.accepted_at)}`;
  }
  if (terms.requires_reacceptance) {
    return `${formatTermsVersion(terms)} waiting for business acceptance`;
  }
  return `${formatTermsVersion(terms)} published`;
}
